/**
 * Authentication utility functions - All calls go through backend API
 */

import { getCsrfToken, clearCsrfCache } from './csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to store session in localStorage
const storeSession = (session) => {
  if (session) {
    localStorage.setItem('auth_token', session.access_token);
    localStorage.setItem('refresh_token', session.refresh_token);
    localStorage.setItem('expires_at', session.expires_at?.toString());
  }
};

// Helper function to store user data in localStorage
const storeUserData = (user) => {
  if (user) {
    localStorage.setItem('user_id', user.id || '');
    localStorage.setItem('user_email', user.email || '');
    localStorage.setItem('user_username', user.username || '');
    localStorage.setItem('user_avatar_url', user.avatar_url || '');
    if (user.created_at) {
      localStorage.setItem('user_created_at', user.created_at);
    }
    if (user.is_seller !== undefined) {
      localStorage.setItem('user_is_seller', user.is_seller ? 'true' : 'false');
    }
    if (user.veriff_status !== undefined) {
      localStorage.setItem('user_veriff_status', user.veriff_status || '');
    }
  }
};

// Helper function to get cached user data from localStorage
export const getCachedUser = () => {
  const id = localStorage.getItem('user_id');
  const email = localStorage.getItem('user_email');
  const username = localStorage.getItem('user_username');
  const avatar_url = localStorage.getItem('user_avatar_url');
  const created_at = localStorage.getItem('user_created_at');
  const is_seller = localStorage.getItem('user_is_seller') === 'true';
  const veriff_status = localStorage.getItem('user_veriff_status');

  if (!id) {
    return null;
  }

  return {
    id,
    email,
    username,
    avatar_url,
    created_at,
    is_seller,
    veriff_status,
  };
};

// Helper function to get stored token
const getStoredToken = () => {
  return localStorage.getItem('auth_token');
};

// Helper function to clear session
const clearSession = () => {
  // Clear all auth-related localStorage items
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('expires_at');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_username');
  localStorage.removeItem('user_avatar_url');
  localStorage.removeItem('user_created_at');
  localStorage.removeItem('user_is_seller');
  localStorage.removeItem('user_veriff_status');
  
  // Clear any cached avatar images
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('avatar_')) {
      localStorage.removeItem(key);
    }
  });
  
  // Dispatch custom event to notify components immediately
  window.dispatchEvent(new CustomEvent('user-logout'));
};

// Sign up with email/password
export const signUp = async (email, password, username) => {
  try {
    const csrfToken = await getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password, username }),
    });

    let data;
    const text = await response.text();
    
    if (!text) {
      return { data: null, error: { message: 'Empty response from server' } };
    }
    
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Failed to parse JSON:', text);
      return { data: null, error: { message: 'Invalid response from server' } };
    }

    if (!response.ok) {
      return { data: null, error: { message: data.error || 'Signup failed' } };
    }

    // Store user data if available
    if (data.user) {
      storeUserData(data.user);
    }

    // Note: Auto-sign-in should be handled by the calling component
    // which has access to the email and password
    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

// Sign in with email/password
export const signIn = async (email, password) => {
  try {
    const csrfToken = await getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
    });

    let data;
    const text = await response.text();
    
    if (!text) {
      return { data: null, error: { message: 'Empty response from server' } };
    }
    
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Failed to parse JSON:', text);
      return { data: null, error: { message: 'Invalid response from server' } };
    }

    if (!response.ok) {
      return { data: null, error: { message: data.error || 'Login failed' } };
    }

    // Store session
    if (data.session) {
      storeSession(data.session);
    }
    
    // Store user data if available
    if (data.user) {
      storeUserData(data.user);
      // Dispatch login event SYNCHRONOUSLY for instant UI update
      // Fire immediately - UserMenu listener will update instantly
      window.dispatchEvent(new CustomEvent('user-login'));
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

// Sign in with OAuth provider (Google, Discord, Steam)
export const signInWithOAuth = async (provider) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/oauth?provider=${provider}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: data.error || 'OAuth initiation failed' } };
    }

    // Redirect to OAuth provider
    if (data.redirectUrl) {
      window.location.href = data.redirectUrl;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

// Sign out
export const signOut = async () => {
  try {
    const token = getStoredToken();
    const csrfToken = await getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

    if (token) {
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Logout error:', data.error);
      }
    }

    clearCsrfCache();
    clearSession();
    
    return { error: null };
  } catch (error) {
    // Clear session even on error
    clearSession();
    return { error: { message: error.message } };
  }
};

// Request deduplication - prevent multiple simultaneous calls
let sessionRequestPromise = null;
let lastSessionCheck = 0;
const SESSION_CHECK_THROTTLE = 2000; // Only check once every 2 seconds

// Get current user session
export const getSession = async () => {
  try {
    const token = getStoredToken();
    
    if (!token) {
      return { session: null, error: null };
    }

    // Throttle requests - prevent spam
    const now = Date.now();
    if (now - lastSessionCheck < SESSION_CHECK_THROTTLE && sessionRequestPromise) {
      return sessionRequestPromise;
    }

    // If there's already a request in flight, return that promise
    if (sessionRequestPromise) {
      return sessionRequestPromise;
    }

    // Create new request promise
    sessionRequestPromise = (async () => {
      lastSessionCheck = now;
      try {
        const response = await fetch(`${API_BASE_URL}/auth/session`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          // Only clear session if it's a 401 (unauthorized)
          // Don't clear on other errors (network, etc.)
          if (response.status === 401) {
            clearSession();
          }
          return { session: null, error: { message: data.error || 'Session invalid' } };
        }

        return { session: { user: data.user }, error: null };
      } catch (error) {
        return { session: null, error: { message: error.message || 'Network error' } };
      } finally {
        // Clear promise after a short delay to allow concurrent calls to reuse it
        setTimeout(() => {
          sessionRequestPromise = null;
        }, 100);
      }
    })();

    return sessionRequestPromise;
  } catch (error) {
    sessionRequestPromise = null;
    return { session: null, error: { message: error.message || 'Network error' } };
  }
};

// Get current user
// Track user IP address
const trackUserIP = async (token) => {
  try {
    await fetch('/api/users/track-ip', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    // Silently fail - IP tracking is not critical
    console.error('Failed to track IP:', error);
  }
};

export const getCurrentUser = async () => {
  const { session, error } = await getSession();
  if (error || !session) {
    return { user: null, error };
  }
  
  // Store user data in localStorage for instant access
  if (session.user) {
    // Only dispatch event if user data actually changed
    const cachedUser = getCachedUser();
    const userChanged = !cachedUser || 
      cachedUser.id !== session.user.id ||
      cachedUser.is_seller !== session.user.is_seller ||
      cachedUser.avatar_url !== session.user.avatar_url;
    
    storeUserData(session.user);
    
    // Only dispatch login event if user data changed (prevents infinite loops)
    if (userChanged) {
      window.dispatchEvent(new CustomEvent('user-login'));
    }
  }
  
  return { user: session.user, error: null };
};

// Refresh session
export const refreshSession = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      return { session: null, error: { message: 'No refresh token available' } };
    }

    const csrfToken = await getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const response = await fetch(`${API_BASE_URL}/auth/session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      clearSession();
      return { session: null, error: { message: data.error || 'Session refresh failed' } };
    }

    // Store new session
    if (data.session) {
      storeSession(data.session);
    }

    return { session: data.session, error: null };
  } catch (error) {
    return { session: null, error: { message: error.message || 'Network error' } };
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  try {
    const token = getStoredToken();
    const expiresAt = localStorage.getItem('expires_at');
    const userId = localStorage.getItem('user_id');
    
    // Need all three to be considered logged in
    if (!token || !expiresAt || !userId) {
      return false;
    }

    // Check if token is expired
    const now = Math.floor(Date.now() / 1000);
    const expires = parseInt(expiresAt);
    
    if (isNaN(expires) || expires <= now) {
      // Clear expired auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('expires_at');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_username');
      localStorage.removeItem('user_avatar_url');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('isAuthenticated error:', error);
    return false;
  }
};

// Upload profile picture
export const uploadProfilePicture = async (userId, file) => {
  try {
    const token = getStoredToken();
    if (!token) {
      return { error: { message: 'Not authenticated' } };
    }

    // Import compression utility dynamically to avoid issues if not available
    const { compressImageToBase64 } = await import('./imageCompression');
    
    // Compress image before upload (max 800x800, quality 0.8)
    const compressedDataUrl = await compressImageToBase64(file, 800, 800, 0.8);
    
    // Extract base64 data (remove data:image/...;base64, prefix)
    const base64Data = compressedDataUrl.split(',')[1];
    
    // Determine file type (always JPEG after compression)
    const fileType = 'image/jpeg';

    const response = await fetch(`${API_BASE_URL}/users/${userId}/avatar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileData: base64Data,
        fileName: file.name,
        fileType: fileType,
      }),
    });

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response:', text.substring(0, 200));
      return { error: { message: 'Server returned an invalid response. Please try again.' } };
    }

    const data = await response.json();

    if (!response.ok) {
      return { error: { message: data.error || 'Upload failed' } };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Upload error:', error);
    return { error: { message: error.message || 'Network error' } };
  }
};

// Generate letter avatar
export const generateLetterAvatar = async (userId, username) => {
  try {
    const token = getStoredToken();
    if (!token) {
      return { error: { message: 'Not authenticated' } };
    }

    const response = await fetch(`${API_BASE_URL}/users/${userId}/avatar/letter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ username }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: { message: data.error || 'Failed to set avatar' } };
    }

    return { data, error: null };
  } catch (error) {
    return { error: { message: error.message || 'Network error' } };
  }
};

// Remove avatar (set to default letter avatar)
export const removeAvatar = async (userId) => {
  try {
    const token = getStoredToken();
    if (!token) {
      return { error: { message: 'Not authenticated' } };
    }

    const response = await fetch(`${API_BASE_URL}/users/${userId}/avatar`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: { message: data.error || 'Failed to remove avatar' } };
    }

    // Clear cached avatar (frontend only)
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(`avatar_${userId}`);
    }

    return { data, error: null };
  } catch (error) {
    return { error: { message: error.message || 'Network error' } };
  }
};
